/**
 * JSON Error Formatting Utilities
 * Provides helper functions to enrich native JSON.parse error messages with
 * line/column location data and a contextual code snippet.
 */

/**
 * Convert a character index within a string to a 1-based line and column.
 * @param {string} text
 * @param {number} index
 * @returns {{ line: number, col: number }}
 */
export function indexToLineCol (text, index) {
	let line = 1
	let col = 1
	for (let i = 0; i < index && i < text.length; i++) {
		if (text[i] === '\n') {
			line++
			col = 1
		} else {
			col++
		}
	}
	return { line, col }
}

/**
 * Build a contextual snippet around an error location with a caret.
 * @param {string} text
 * @param {number} errorLine 1-based line
 * @param {number} errorCol 1-based column
 * @param {number} contextRadius number of lines of context before & after
 * @returns {string}
 */
export function buildErrorSnippet (text, errorLine, errorCol, contextRadius = 2) {
	const lines = text.split(/\n/)
	const startLine = Math.max(1, errorLine - contextRadius)
	const endLine = Math.min(lines.length, errorLine + contextRadius)
	const numberWidth = String(endLine).length
	const snippetLines = []
	for (let ln = startLine; ln <= endLine; ln++) {
		const prefix = (ln === errorLine ? '>' : ' ') + String(ln).padStart(numberWidth, ' ') + ' | '
		snippetLines.push(prefix + lines[ln - 1])
		if (ln === errorLine) {
			const caretPrefix = ' '.repeat(prefix.length)
			const caretLine = caretPrefix + ' '.repeat(Math.max(0, errorCol - 1)) + '^'
			snippetLines.push(caretLine)
		}
	}
	return snippetLines.join('\n')
}

/**
 * Format a native JSON.parse error with line/column and snippet.
 * @param {Error} error The thrown parse error
 * @param {string} source The JSON source string
 * @param {object} [options]
 * @param {string} [options.prefix='Invalid JSON'] Prefix label
 * @returns {string}
 */
export function formatJsonError (error, source, { prefix = 'Invalid JSON' } = {}) {
	if (!error || !source) return prefix
	let message = error.message || 'Unknown JSON parsing error'
	const positionMatch = message.match(/at position (\d+)/i)
	if (positionMatch) {
		const index = parseInt(positionMatch[1], 10)
		const { line, col } = indexToLineCol(source, index)
		const snippet = buildErrorSnippet(source, line, col)
		message = `${prefix} (line ${line}, column ${col}): ${message}\n${snippet}`
	} else {
		message = `${prefix}: ${message}`
	}
	return message
}

/**
 * Append JSON context (if available) to an existing base message.
 * Used when you already have a domain-specific error description.
 * @param {string} baseMessage
 * @param {string} source
 * @param {Error} [originalError]
 * @returns {string}
 */
export function appendJsonErrorContext (baseMessage, source, originalError) {
	if (!source || typeof source !== 'string') return baseMessage
	const searchIn = originalError && originalError.message
		? baseMessage + ' ' + originalError.message
		: baseMessage
	const positionMatch = searchIn.match(/at position (\d+)/i)
	if (positionMatch) {
		const index = parseInt(positionMatch[1], 10)
		const { line, col } = indexToLineCol(source, index)
		const snippet = buildErrorSnippet(source, line, col)
		return `${baseMessage}\nLocation: line ${line}, column ${col}\n${snippet}`
	}
	return baseMessage
}
